import { IconEllipsis, IconLock } from "@studyplus/boron-ui";
import classNames from "classnames";
import * as React from "react";
import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverProvider,
  PopoverTrigger,
} from "../../components/general/Popover/Popover";
import {
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipTrigger,
} from "../../components/general/Tooltip/Tooltip";
import type { DrillContentItem, StudentWithAnswers } from "./types";

// TODO: 現状 OpenAPI 上の itemType が `string | null` で enum 定義されていないため、
// 暫定で `form_understanding_text` を点数入力型として文字列決め打ちで扱う。
// 理想はバックエンドに itemType を OpenAPI の enum として定義してもらい、`./types.ts` に集約すること。
const SCORE_INPUT_ITEM_TYPE = "form_understanding_text";

const isScoreInputItem = (itemType: string | null | undefined): boolean =>
  itemType === SCORE_INPUT_ITEM_TYPE;

const formatAnswer = (
  answers: string[] | undefined,
  itemType: string | null | undefined,
): string => {
  if (!answers || answers.length === 0) {
    return "";
  }
  if (isScoreInputItem(itemType)) {
    if (answers.every((a) => a === "")) {
      return "-";
    }
    // answers[0] = 総問題数, answers[1] = 正答数 → 「正答数/総問題数」で表示
    return `${answers[1]}/${answers[0]}`;
  }
  return answers.join(", ");
};

type Props = {
  students: StudentWithAnswers[];
  selectedItem: DrillContentItem | undefined;
  allowStudiedAnswer: boolean;
  // WEB-1742: 仕様明確化まで「未回答を非表示」props を一時停止
  // 復活時はこの 2 props と下の displayStudents filter・チェックボックス UI をアンコメントするだけで戻る。
  // 詳細: https://www.notion.so/3675fd6b740381fe90b1f33c8ccf5651
  // hideUnanswered: boolean;
  // onHideUnansweredChange: (value: boolean) => void;
};

// SORT-02: 連携中・招待中かつ本棚登録済み
const isActiveStudent = (s: StudentWithAnswers) =>
  (s.passcodeStatus === "active" || s.passcodeStatus === "invited") &&
  s.shelved;

const sortStudentsDefault = (students: StudentWithAnswers[]) => {
  return [...students].sort((a, b) => {
    const aActive = isActiveStudent(a);
    const bActive = isActiveStudent(b);

    if (aActive !== bActive) {
      return aActive ? -1 : 1;
    }

    // 同グループ内は登録が新しい順（lastShelvedAt 降順）
    return (b.lastShelvedAt ?? "").localeCompare(a.lastShelvedAt ?? "");
  });
};

// DISP-01: フリープラン判定
const isFreePlan = (student: StudentWithAnswers) => !student.hasPaidService;

// DISP-02: 本棚未登録 or 連携停止中はグレーアウト（DISP-01 と独立評価、フリープランでも適用）
const isGreyedOutStudent = (student: StudentWithAnswers) =>
  !student.shelved || student.passcodeStatus === "inactive";

// WEB-1742: 仕様明確化まで一時停止
// 未回答判定: 全アイテムの回答が空
// const isUnanswered = (student: StudentWithAnswers) =>
//   student.items.every(
//     (item) =>
//       !item.answers ||
//       item.answers.length === 0 ||
//       item.answers.every((a) => a === ""),
//   );

export const StudentAnswerTable = ({
  students,
  selectedItem,
  allowStudiedAnswer,
}: Props) => {
  const sortedStudents = React.useMemo(
    () => sortStudentsDefault(students),
    [students],
  );

  // WEB-1742: 仕様明確化まで「未回答を非表示」を一時停止。
  // 復活時はこの passthru を削除し、下の useMemo + filter をアンコメントする。
  const displayStudents = sortedStudents;
  // const displayStudents = React.useMemo(() => {
  //   if (!hideUnanswered) {
  //     return sortedStudents;
  //   }
  //   return sortedStudents.filter((s) => !isUnanswered(s));
  // }, [sortedStudents, hideUnanswered]);

  if (sortedStudents.length === 0) {
    return (
      <div className="my-7 text-center">該当する生徒が見つかりませんでした</div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div>
        {/* WEB-1742: 仕様明確化まで「未回答を非表示」ツールバーを一時停止 */}
        {/* <div className="mb-2.5 flex items-center gap-4">
          <div className="ml-auto flex items-center gap-4">
            <label className="flex cursor-pointer items-center gap-1 text-sm font-medium text-black">
              <input
                type="checkbox"
                checked={hideUnanswered}
                onChange={(e) => onHideUnansweredChange(e.target.checked)}
                className="cursor-pointer"
              />
              未回答を非表示
            </label>
          </div>
        </div> */}

        {displayStudents.length === 0 ? (
          <div className="my-7 text-center">
            該当する生徒が見つかりませんでした
          </div>
        ) : (
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            }}
          >
            {displayStudents.map((student) => (
              <StudentCard
                key={student.publicId}
                student={student}
                currentItem={selectedItem}
                allowStudiedAnswer={allowStudiedAnswer}
              />
            ))}
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

type StudentCardProps = {
  student: StudentWithAnswers;
  currentItem: DrillContentItem | undefined;
  allowStudiedAnswer: boolean;
};

const StudentCard = ({
  student,
  currentItem,
  allowStudiedAnswer,
}: StudentCardProps) => {
  const isFree = isFreePlan(student);
  const isGreyedOut = isGreyedOutStudent(student);

  const currentAnswer = student.items.find(
    (a) => a.publicId === currentItem?.publicId,
  );
  const answer = formatAnswer(currentAnswer?.answers, currentItem?.itemType);
  const correct = currentAnswer?.correct;
  const hasIncorrect = correct === false;

  const headerBgClass = isGreyedOut ? "bg-gray-100" : "bg-white";
  const bodyBgClass = isGreyedOut
    ? "bg-gray-100"
    : hasIncorrect
      ? "bg-darkred-100"
      : "bg-white";

  const studentName = `${student.lastName} ${student.firstName}`;

  return (
    <div className="flex flex-col rounded-[4px]">
      {/* ヘッダー: 生徒名 + 定着度バッジ */}
      <div
        className={classNames(
          "flex items-center justify-between gap-2 rounded-t-[4px] border border-solid border-gray-400 px-4 py-2.5",
          headerBgClass,
        )}
      >
        <StudentName name={studentName} isGreyedOut={isGreyedOut} />
        {student.retentionLevel && (
          <RetentionLevelBadge
            level={student.retentionLevel}
            allowStudiedAnswer={allowStudiedAnswer}
          />
        )}
      </div>

      {/* ボディ: 回答エリア */}
      <div
        className={classNames(
          "flex min-h-[44px] items-center gap-2 rounded-b-[4px] border-0 border-r border-b border-l border-solid border-gray-400 px-4 py-2.5",
          bodyBgClass,
        )}
      >
        {isFree ? (
          <FreePlanLockIcon />
        ) : (
          <>
            {correct != null && <CorrectIcon correct={correct} />}
            {answer && (
              <span className="min-w-0 flex-1 break-words text-lg font-medium leading-[1.4] text-black">
                {answer}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const StudentName = ({
  name,
  isGreyedOut,
}: {
  name: string;
  isGreyedOut: boolean;
}) => {
  const spanRef = React.useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);
  const [open, setOpen] = React.useState(false);

  React.useLayoutEffect(() => {
    const el = spanRef.current;
    if (!el) {
      return;
    }
    const check = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tooltip open={isTruncated && open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <span
          ref={spanRef}
          className={classNames(
            "min-w-0 flex-1 truncate text-lg font-bold leading-[1.3] text-black",
            isGreyedOut && "opacity-50",
          )}
        >
          {name}
        </span>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent side="top">{name}</TooltipContent>
      </TooltipPortal>
    </Tooltip>
  );
};

const CorrectIcon = ({ correct }: { correct: boolean }) => {
  if (correct) {
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-label="正解"
        className="shrink-0"
      >
        <circle cx="8" cy="8" r="5.75" stroke="#2F6AED" strokeWidth="2.5" />
      </svg>
    );
  }
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="不正解"
      className="shrink-0"
    >
      <path
        d="M13.6598 2.34009L2.33984 13.6601"
        stroke="#F15360"
        strokeWidth="2.5"
        strokeMiterlimit="10"
      />
      <path
        d="M13.6598 13.6601L2.33984 2.34009"
        stroke="#F15360"
        strokeWidth="2.5"
        strokeMiterlimit="10"
      />
    </svg>
  );
};

type RetentionLevel = Exclude<StudentWithAnswers["retentionLevel"], null>;
type DisplayedRetentionLevel = Exclude<RetentionLevel, "not_studied">;

const retentionLevelConfig: Record<
  DisplayedRetentionLevel,
  {
    label: string;
    textColor: string;
    borderColor: string;
    withoutRetentionPrefix?: boolean;
  }
> = {
  perfect: {
    label: "S",
    textColor: "text-blue-400",
    borderColor: "border-blue-400",
  },
  established: {
    label: "A",
    textColor: "text-yellow-400",
    borderColor: "border-yellow-400",
  },
  weak: {
    label: "B",
    textColor: "text-darkred-500",
    borderColor: "border-darkred-500",
  },
  studied: {
    label: "完了",
    textColor: "text-green-400",
    borderColor: "border-green-400",
    withoutRetentionPrefix: true,
  },
};

const FreePlanLockIcon = () => (
  <PopoverProvider>
    <Popover>
      <PopoverTrigger asChild>
        <span className="inline-flex cursor-default items-center gap-x-1">
          <IconLock
            role="img"
            aria-label="学習管理サービス未連携"
            className="h-[24px] w-[24px] text-gray-600"
          />
          <IconEllipsis
            role="img"
            aria-label="回答非表示"
            className="h-[24px] w-[24px] rotate-90 text-gray-600"
          />
        </span>
      </PopoverTrigger>
      <PopoverPortal>
        <PopoverContent side="top">
          学習管理サービスを追加すると
          <br />
          生徒の回答が閲覧できます
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  </PopoverProvider>
);

const RetentionLevelBadge = ({
  level,
  allowStudiedAnswer,
}: {
  level: RetentionLevel;
  allowStudiedAnswer: boolean;
}) => {
  // 未学習はバッジを表示しない
  if (level === "not_studied") {
    return null;
  }
  // DISP-03: allow=false のセクションでは「完了」バッジを非表示にする
  if (level === "studied" && !allowStudiedAnswer) {
    return null;
  }

  const config = retentionLevelConfig[level];
  const isPerfect = level === "perfect";

  if (config.withoutRetentionPrefix) {
    return (
      <span
        className={classNames(
          "shrink-0 rounded-[4px] border border-solid bg-white px-[10px] py-[2px] text-[10px] font-bold leading-[1.3]",
          config.textColor,
          config.borderColor,
        )}
      >
        {config.label}
      </span>
    );
  }

  return (
    <span
      className={classNames(
        "flex shrink-0 flex-nowrap items-center gap-[2px] rounded-[4px] border border-solid bg-white leading-[1.3]",
        isPerfect ? "px-[5px]" : "px-[4px]",
        config.textColor,
        config.borderColor,
      )}
    >
      <span className="text-[10px] font-bold whitespace-nowrap">定着度</span>
      <span className="font-[Lato,sans-serif] text-[14px] font-bold">
        {config.label}
      </span>
    </span>
  );
};
